import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { Organization, OrganizationUsers, OrgUserRoles } from '@models';

import { appstraxAuth, User } from '@appstrax/services/auth';
import { appstraxStorage } from '@appstrax/services/storage';
import { WorkspaceFacade } from '@state';
import { OrganizationService, OrganizationUsersService } from '@services';
import { applyLogoInputSelection } from '../../../utils/logo-file.util';

@Component({
  selector: 'app-create-organization',
  templateUrl: './create-organization.page.html',
  styleUrls: ['./create-organization.page.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
})
export class CreateOrganizationPage {
  private orgUsersService = inject(OrganizationUsersService);
  private organizationService = inject(OrganizationService);
  isLoading: boolean = false;
  errorMessage: string = '';
  logoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  organization: Organization = new Organization();
  orgUser: OrganizationUsers = new OrganizationUsers();
  backLink: string = '/sign-up';
  isEditMode: boolean = false;
  authStep: number | undefined;
  readonly setupSteps = [1, 2, 3];

  constructor(
    private store: WorkspaceFacade,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.authStep = this.route.snapshot.data['authStep'] as number | undefined;
    const from = this.route.snapshot.queryParamMap.get('from');
    this.backLink = from === 'home' ? '/home' : '/sign-up';
    const editId = this.route.snapshot.queryParamMap.get('editId');
    if (editId) {
      this.isEditMode = true;
      // Prefill organization and logo
      const existing = this.store.organizations().find((o) => o.id === editId);
      if (existing) {
        this.organization = existing;
        this.logoPreviewUrl = existing.logoUrl || null;
      } else {
        this.organizationService.findById(editId).then((org) => {
          this.organization = org;
          this.logoPreviewUrl = org.logoUrl || null;
        }).catch(() => {
          this.errorMessage = 'Failed to load organization';
        });
      }
    }
  }

  async createOrganization(): Promise<void> {
    if (!this.isFormValid()) {
      this.errorMessage = 'Please enter all required fields';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      if (this.logoFile) {
        await this.uploadOrganizationLogo(this.logoFile);
      }

      if (this.isEditMode) {
        this.organization = await this.organizationService.save(this.organization);
        this.store.upsertEntities({ organizations: [this.organization] });
        this.router.navigate(['/home']);
      } else {
        const user: User = await appstraxAuth.getUser();
        this.organization = await this.organizationService.save(this.organization);
        this.orgUser.organizationId = this.organization.id;
        this.orgUser.userId = user.id;
        this.orgUser.role = OrgUserRoles.ADMIN;
        this.orgUser = await this.orgUsersService.save(this.orgUser);
        if (this.organization && this.orgUser) {
          this.store.upsertEntities({ organizations: [this.organization], orgUsers: [this.orgUser] });
          this.store.selectProject(null, this.organization.id);
          this.router.navigate(['/create-project'], {
            queryParams: {
              from: 'create-organization',
              organizationId: this.organization.id,
            },
          });
        }
      }
    } catch (error: any) {
      this.errorMessage = error.message;
    } finally {
      this.isLoading = false;
    }
  }

  public async uploadOrganizationLogo(file: File) {
    const response = await appstraxStorage.uploadFile(
      file,
      'organizationLogos'
    );
    this.organization.logoUrl = response.downloadUrl;
  }

  public onLogoSelected(event: Event) {
    applyLogoInputSelection(event, (file, previewUrl) => {
      this.logoFile = file;
      this.logoPreviewUrl = previewUrl;
    });
  }

  public isFormValid(): boolean {
    return (
      this.organization.name != '' &&
      this.organization.description != '' &&
      this.organization.country != '' &&
      this.organization.email != '' &&
      this.organization.address != '' &&
      this.organization.city != '' &&
      this.organization.state != '' &&
      this.organization.zip != '' &&
      this.organization.phone != ''
    );
  }
}
